import { browserDate } from 'utils/time'
import { filterOffersToBounds } from './offerLocations';

// Helper function to extract numeric distance from "X miles away" format
export const extractDistanceValue = (distanceStr) => {
  if (!distanceStr || typeof distanceStr !== 'string') return null;
  const distanceLower = distanceStr.trim().toLowerCase();
  
  // Check if it's a known non-distance value
  if (distanceLower === 'global' || 
      distanceLower === 'united states' || 
      distanceLower === 'us' ||
      distanceLower === 'unites states') {
    return null;
  }
  
  // Match pattern like "5.2 miles away" or "0 miles away"
  const match = distanceLower.match(/^([\d.]+)\s+miles\s+away$/);
  return match ? parseFloat(match[1]) : null;
};

const sortByDistance = (offers) => {
  return [...offers].sort((a, b) => {
    const distanceA = extractDistanceValue(a.distance) ?? Infinity;
    const distanceB = extractDistanceValue(b.distance) ?? Infinity;
    return distanceA - distanceB;
  });
};

/**
 * Apply filter to offers based on selected filter type
 * @param {Array} offers - Array of offer objects
 * @param {string} selectedFilter - Filter type: 'none', 'local', 'nearMe', 'expireSoon'
 * @param {boolean} isMapVisible - Whether map view is visible (applies additional distance filter)
 * @param {number|null} radius - User search radius in miles; null means trust backend per-offer radius
 * @returns {Array} Filtered and sorted offers
 */
export const filterOffers = (offers, selectedFilter = 'none', isMapVisible = false, radius = null) => {
  let filtered = [...offers];

  // Apply selected filter
  switch (selectedFilter) {
    case 'local':
      filtered = offers.filter(offer => extractDistanceValue(offer.distance) !== null);
      if (radius != null) {
        filtered = filtered.filter(offer => {
          const distanceValue = extractDistanceValue(offer.distance);
          return distanceValue !== null && distanceValue <= radius;
        });
      }
      break;
    case 'nearMe':
      filtered = offers.filter(offer => {
        const distanceValue = extractDistanceValue(offer.distance);
        return distanceValue !== null && (radius == null || distanceValue <= radius);
      });
      filtered = sortByDistance(filtered);
      break;
    case 'expireSoon':
      // Filter offers expiring within 7 days (and haven't expired yet)
      filtered = offers.filter(offer => {
        if (!offer.endDate) return false;
        
        try {
          const endDate = browserDate(offer.endDate);
          // Check if date is valid
          if (isNaN(endDate.getTime())) return false;
          
          const now = new Date();
          const diffTime = endDate.getTime() - now.getTime();
          const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          
          // Only show offers that expire within 7 days and haven't expired yet
          return daysRemaining <= 7 && daysRemaining >= 0;
        } catch (error) {
          console.warn('Invalid endDate format:', offer.endDate, error);
          return false;
        }
      });
      break;
    default:
      // No filter applied - show all offers
      filtered = offers;
  }

  // When map is visible, optionally cap by user radius; otherwise show backend-filtered results
  if (isMapVisible) {
    if (radius != null) {
      filtered = filtered.filter(offer => {
        const distanceValue = extractDistanceValue(offer.distance);
        return distanceValue !== null && distanceValue <= radius;
      });
    }
    if (selectedFilter === 'nearMe') {
      filtered = sortByDistance(filtered);
    }
  }

  return filtered;
};

export const filterMapOffers = (offers, bounds) => filterOffersToBounds(offers, bounds);
